import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Notifications,
  Language,
  Palette,
  Security,
  DeleteForever,
  Save,
  CheckCircle
} from '@mui/icons-material';
import SectionHeader from '@shared/SectionHeader';
import { useAuth } from '@contexts/AuthContext';

interface NotificationSettings {
  emailAppointments: boolean;
  emailMessages: boolean;
  emailInvoices: boolean;
  emailAdoptions: boolean;
  pushAppointments: boolean;
  pushMessages: boolean;
  pushInvoices: boolean;
  pushAdoptions: boolean;
}

const SettingsPage: React.FC = () => {
  const { user, toggleTheme } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [language, setLanguage] = useState('ro');
  const [theme, setTheme] = useState<'light' | 'dark' | 'auto'>('auto');
  
  const [notifications, setNotifications] = useState<NotificationSettings>({
    emailAppointments: true,
    emailMessages: true,
    emailInvoices: true,
    emailAdoptions: false,
    pushAppointments: true,
    pushMessages: true,
    pushInvoices: false,
    pushAdoptions: false
  });

  useEffect(() => {
    // Load user settings
    // TODO: Replace with actual API call
    console.log('Loading user settings for:', user?.id);
  }, [user?.id]);

  const handleNotificationChange = (key: keyof NotificationSettings) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual API call
      console.log('Saving settings:', {
        language,
        theme,
        notifications
      });

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Eroare la salvarea setărilor. Te rugăm să încerci din nou.');
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'auto') => {
    setTheme(newTheme);
    if (newTheme !== 'auto' && toggleTheme) {
      toggleTheme();
    }
  };

  const handleDeleteAccount = async () => {
    // TODO: Implement account deletion
    console.log('Deleting account for:', user?.id);
    setDeleteDialogOpen(false);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <SectionHeader
        title="Setări & Preferințe"
        subtitle="Personalizează experiența ta și gestionează preferințele contului"
      />

      {success && (
        <Alert 
          severity="success" 
          icon={<CheckCircle />}
          sx={{ mb: 3 }}
          onClose={() => setSuccess(false)}
        >
          Setările au fost salvate cu succes!
        </Alert>
      )}

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Notifications Settings */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Notifications color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Notificări
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Alege ce notificări dorești să primești
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
            Notificări Email
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.emailAppointments}
                  onChange={() => handleNotificationChange('emailAppointments')}
                />
              }
              label="Programări și confirmări"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.emailMessages}
                  onChange={() => handleNotificationChange('emailMessages')}
                />
              }
              label="Mesaje noi"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.emailInvoices}
                  onChange={() => handleNotificationChange('emailInvoices')}
                />
              }
              label="Facturi și plăți"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.emailAdoptions}
                  onChange={() => handleNotificationChange('emailAdoptions')}
                />
              }
              label="Adoptii și campanii"
            />
          </FormGroup>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Notificări Push
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.pushAppointments}
                  onChange={() => handleNotificationChange('pushAppointments')}
                />
              }
              label="Programări și confirmări"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.pushMessages}
                  onChange={() => handleNotificationChange('pushMessages')}
                />
              }
              label="Mesaje noi"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.pushInvoices}
                  onChange={() => handleNotificationChange('pushInvoices')}
                />
              }
              label="Facturi și plăți"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.pushAdoptions}
                  onChange={() => handleNotificationChange('pushAdoptions')}
                />
              }
              label="Adoptii și campanii"
            />
          </FormGroup>
        </CardContent>
      </Card>

      {/* Language & Theme Settings */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Language color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Limbă și Aspect
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Personalizează limba și tema aplicației
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Limbă</InputLabel>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              label="Limbă"
            >
              <MenuItem value="ro">Română</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </Select>
          </FormControl>

          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Palette color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Temă
            </Typography>
          </Box>

          <FormControl fullWidth>
            <InputLabel>Temă</InputLabel>
            <Select
              value={theme}
              onChange={(e) => handleThemeChange(e.target.value as 'light' | 'dark' | 'auto')}
              label="Temă"
            >
              <MenuItem value="light">Luminos</MenuItem>
              <MenuItem value="dark">Întunecat</MenuItem>
              <MenuItem value="auto">Automat (sistem)</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Privacy & Security */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Security color="primary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Confidențialitate și Securitate
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gestionează setările de confidențialitate
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ bgcolor: 'error.light', p: 2, borderRadius: 2, mb: 2 }}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <DeleteForever sx={{ color: 'error.dark' }} />
              <Typography variant="subtitle1" fontWeight={600} color="error.dark">
                Zona Periculoasă
              </Typography>
            </Box>
            <Typography variant="body2" color="error.dark" paragraph>
              Ștergerea contului este permanentă și nu poate fi anulată. Toate datele tale, inclusiv animalele, programările și documentele vor fi șterse definitiv.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteForever />}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Șterge contul permanent
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSaveSettings}
          disabled={loading}
          sx={{ textTransform: 'none', borderRadius: 2, minWidth: 150 }}
        >
          {loading ? 'Se salvează...' : 'Salvează modificările'}
        </Button>
      </Box>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600} color="error">
            Confirmare ștergere cont
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ești sigur că vrei să îți ștergi contul? Această acțiune este permanentă și nu poate fi anulată.
            Toate datele tale, inclusiv:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 2, color: 'text.secondary' }}>
            <li>Informațiile despre animalele tale</li>
            <li>Istoricul programărilor și vizitelor</li>
            <li>Documentele medicale și analizele</li>
            <li>Mesajele și notificările</li>
            <li>Facturi și plăți</li>
          </Box>
          <DialogContentText sx={{ mt: 2, fontWeight: 600, color: 'error.main' }}>
            vor fi șterse definitiv.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Anulează
          </Button>
          <Button 
            onClick={handleDeleteAccount}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Da, șterge contul
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettingsPage;

